import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import ImageDropZone from '../common/ImageDropZone';
import MediaGrid from '../hive/MediaGrid';
import CopyLinkButton from '../hive/CopyLinkButton';

/**
 * Sales & Marketing → Contests (Jenn, 2026-10-24: "we do member engagement and
 * stickiness in the sales and marketing control space"). Members-only contests
 * are posted to My Hive and show as a 4th bubble on the member dashboard while
 * they run; members tick "This is my contest entry" on a Hive post or comment.
 * Entries list here any time; one To-Do comes when a contest ends.
 * Lead magnet (public) contests take entries on a sign-up page (coming next).
 */
const BLANK = { name: '', audience: 'members', message: '', details: '', prize: '', rules: '', imageUrl: '', imageAlt: '', startsOn: '', endsOn: '', winnerOn: '', winnerMethod: 'pick', initiativeId: '', eventWords: '', notEligible: [], points: { live: 2, replay: 1, engage: 1, picture: 2 } };
const POINT_LABELS = [['live', 'Came to the live event'], ['replay', 'Watched the replay (not for a session they came to live)'], ['engage', 'Liked or commented on a contest post in My Hive (once per post)'], ['picture', 'Posted a contest entry with a picture or video (once per week)']];
const STATUS = { running: 'Running now', upcoming: 'Coming up', ended: 'Ended' };
const box = { width: '100%', fontSize: 15, padding: 9, borderRadius: 10, border: '1px solid #d9cfc4', marginTop: 4, boxSizing: 'border-box', fontFamily: 'inherit' };
const fmt = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

function ContestForm({ start, magnets, people, onSaved, onCancel }) {
  const [f, setF] = useState({ ...BLANK, ...start });
  const toggleNotEligible = (id) => setF(x => {
    const list = x.notEligible || [];
    return { ...x, notEligible: list.includes(id) ? list.filter(v => v !== id) : [...list, id] };
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = { ...f, initiativeId: f.audience === 'public' ? (f.initiativeId || null) : null };
      const r = f.id ? await api.put(`/community/contests/manage/${f.id}`, body) : await api.post('/community/contests/manage', body);
      onSaved(r.data.contest);
    } catch (e) { setMsg(e.response?.data?.error || 'Could not save. Please try again.'); }
    setBusy(false);
  };
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <p className="q">{f.id ? 'Edit contest' : 'New contest'}</p>
      {msg && <div className="note">{msg}</div>}
      <label className="qs" style={{ display: 'block', marginTop: 8 }}>Contest name<input value={f.name} onChange={e => set('name', e.target.value)} style={box} placeholder="Example: Fireside Chats with Trish" /></label>
      <div className="qs" style={{ marginTop: 10 }}>Who can enter</div>
      <div className="row" style={{ gap: 14, marginTop: 4 }}>
        <label className="qs" style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={f.audience === 'members'} onChange={() => set('audience', 'members')} /> Members only (in My Hive)</label>
        <label className="qs" style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={f.audience === 'public'} onChange={() => set('audience', 'public')} /> Lead magnet (open to the public)</label>
      </div>
      {f.audience === 'public' && (
        <label className="qs" style={{ display: 'block', marginTop: 8 }}>Part of this lead magnet (optional)
          <select value={f.initiativeId || ''} onChange={e => set('initiativeId', e.target.value)} style={box}>
            <option value="">Not linked</option>
            {magnets.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <span style={{ display: 'block', marginTop: 4 }}>The public entry page (name, email, link to their post) is the next piece to be built.</span>
        </label>
      )}
      <div className="row" style={{ gap: 10, marginTop: 8 }}>
        <label className="qs" style={{ flex: 1, minWidth: 140 }}>Starts<input type="date" value={f.startsOn || ''} onChange={e => set('startsOn', e.target.value)} style={box} /></label>
        <label className="qs" style={{ flex: 1, minWidth: 140 }}>Ends<input type="date" value={f.endsOn || ''} onChange={e => set('endsOn', e.target.value)} style={box} /></label>
        <label className="qs" style={{ flex: 1, minWidth: 140 }}>Winner chosen on<input type="date" value={f.winnerOn || ''} onChange={e => set('winnerOn', e.target.value)} style={box} /></label>
      </div>
      <label className="qs" style={{ display: 'block', marginTop: 8 }}>Short line for the dashboard bubble<input value={f.message || ''} onChange={e => set('message', e.target.value)} maxLength={300} style={box} placeholder="Example: Do this week's experiment for a chance to win a private consult with Trish." /></label>
      <label className="qs" style={{ display: 'block', marginTop: 8 }}>The details: how to enter, the rules (this is the Hive post)<textarea value={f.details || ''} onChange={e => set('details', e.target.value)} rows={7} style={box} /></label>
      <label className="qs" style={{ display: 'block', marginTop: 8 }}>What the winner gets<textarea value={f.prize || ''} onChange={e => set('prize', e.target.value)} rows={5} style={box} /></label>
      <label className="qs" style={{ display: 'block', marginTop: 8 }}>
        Contest rules (required — always shown on the Hive post)
        <span style={{ display: 'block', marginTop: 2 }}>Include who can enter, the dates, how to enter, how the winner is chosen and when, and how the winner is told.</span>
        <textarea value={f.rules || ''} onChange={e => set('rules', e.target.value)} rows={9} style={box} />
      </label>
      <div className="qs" style={{ marginTop: 10 }}>Picture (shows on the bubble and the Hive post)</div>
      <div className="qs" style={{ marginTop: 2 }}>
        Best size: <b>1080 × 1350 pixels</b> (portrait, the same as an Instagram post). In Canva: Create a design → Custom size → 1080 × 1350 px.
        The Hive post shows the whole picture; the dashboard bubble shows a small square from the middle, so keep the face or main image centred.
      </div>
      <div style={{ maxWidth: 320, marginTop: 4 }}>
        <ImageDropZone currentUrl={f.imageUrl || null} onUploaded={(u) => set('imageUrl', u)} onClear={() => set('imageUrl', '')} />
      </div>
      <label className="qs" style={{ display: 'block', marginTop: 8 }}>Describe the picture<input value={f.imageAlt || ''} onChange={e => set('imageAlt', e.target.value)} style={box} placeholder="Example: Trish smiling, with the words Fireside Chats with Trish" /></label>
      <div className="qs" style={{ marginTop: 10 }}>Not eligible to win (tick anyone on Team365 who can't win this one)</div>
      <div className="row" style={{ gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
        {people.map(p => (
          <label key={p.id} className="qs" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={(f.notEligible || []).includes(p.id)} onChange={() => toggleNotEligible(p.id)} /> {p.name}
          </label>
        ))}
      </div>
      <div className="qs" style={{ marginTop: 10 }}>How the winner is chosen</div>
      <div className="row" style={{ gap: 14, marginTop: 4 }}>
        <label className="qs" style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={f.winnerMethod === 'pick'} onChange={() => set('winnerMethod', 'pick')} /> We pick from the entries</label>
        <label className="qs" style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={f.winnerMethod === 'points'} onChange={() => set('winnerMethod', 'points')} /> Most points wins</label>
      </div>
      {f.winnerMethod === 'points' && (
        <div style={{ marginTop: 8, padding: 10, border: '1px solid #eee5da', borderRadius: 10 }}>
          <label className="qs" style={{ display: 'block' }}>Live events and replays count when their name includes
            <input value={f.eventWords || ''} onChange={e => set('eventWords', e.target.value)} style={box} placeholder="Example: Fireside" />
          </label>
          {POINT_LABELS.map(([k, label]) => (
            <label key={k} className="qs" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <input type="number" min="0" max="100" value={(f.points || {})[k] ?? ''} onChange={e => set('points', { ...(f.points || {}), [k]: e.target.value })} style={{ ...box, width: 70, marginTop: 0 }} />
              points: {label}
            </label>
          ))}
        </div>
      )}
      <div className="row" style={{ gap: 8, marginTop: 14 }}>
        <button type="button" className="btn p s" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn s" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function Entries({ contest, onChanged }) {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [picked, setPicked] = useState(null);
  const load = useCallback(() => {
    api.get(`/community/contests/manage/${contest.id}/entries`).then(r => setData(r.data)).catch(() => setMsg('Could not load the entries.'));
  }, [contest.id]);
  useEffect(() => { load(); }, [load]);
  const winner = async (entryId) => {
    try { await api.post(`/community/contests/manage/${contest.id}/winner`, { entryId }); setPicked(null); load(); onChanged(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not save the winner.'); }
  };
  const random = async () => {
    try { const r = await api.post(`/community/contests/manage/${contest.id}/random`); setPicked(r.data); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not pick one.'); }
  };
  if (!data) return <p className="qs">{msg || 'Loading entries…'}</p>;
  const winnerId = data.contest.winnerEntryId;
  return (
    <div style={{ marginTop: 10 }}>
      {msg && <div className="note">{msg}</div>}
      {data.entries.length === 0 ? <p className="qs">No entries yet.</p> : (
        <>
          <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <button type="button" className="btn s" onClick={random}>Pick one at random</button>
            <span className="qs">{new Set(data.entries.map(e => e.memberId)).size} people · {data.entries.length} entries</span>
          </div>
          {picked && (
            <div className="note">
              Picked at random from {picked.from} {picked.from === 1 ? 'person' : 'people'}: <b>{data.entries.find(e => e.id === picked.entryId)?.memberName}</b>.{' '}
              <button type="button" className="btn p s" onClick={() => winner(picked.entryId)}>Make this the winner</button>{' '}
              <button type="button" className="btn s" onClick={() => setPicked(null)}>Not this time</button>
            </div>
          )}
          {data.entries.map(e => (
            <div key={e.id} style={{ borderTop: '1px solid #eee5da', padding: '10px 0', background: e.id === winnerId ? '#f6efe6' : 'transparent' }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span><b>{e.memberName}</b> <span className="qs">· {new Date(e.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · on a {e.kind}</span>{e.id === winnerId && <b style={{ color: '#C4714A' }}> · 🏆 Winner</b>}</span>
                <span className="row" style={{ gap: 6 }}>
                  {e.threadPostId && <Link className="btn s" to={`/dashboard/discussion/${e.threadPostId}`}>Open</Link>}
                  {e.id === winnerId
                    ? <button type="button" className="btn s" onClick={() => winner(null)}>Undo winner</button>
                    : <button type="button" className="btn s" onClick={() => winner(e.id)}>Winner</button>}
                </span>
              </div>
              {e.body && <p className="qs" style={{ margin: '4px 0 0', whiteSpace: 'pre-line' }}>{e.body.slice(0, 280)}</p>}
              {e.media.length > 0 && <div style={{ maxWidth: 360 }}><MediaGrid media={e.media} size="comment" /></div>}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function Points({ contest, onChanged }) {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [link, setLink] = useState('');
  const load = useCallback(() => {
    api.get(`/community/contests/manage/${contest.id}/points`).then(r => setData(r.data)).catch(() => setMsg('Could not work out the points.'));
  }, [contest.id]);
  useEffect(() => { load(); }, [load]);
  const addPost = async () => {
    setMsg(null);
    try { await api.post(`/community/contests/manage/${contest.id}/posts`, { link }); setLink(''); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not add that post.'); }
  };
  const removePost = async (postId) => { await api.delete(`/community/contests/manage/${contest.id}/posts/${postId}`); load(); };
  const winner = async (memberId) => {
    try { await api.post(`/community/contests/manage/${contest.id}/winner-member`, { memberId }); load(); onChanged(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not save the winner.'); }
  };
  if (!data) return <p className="qs">{msg || 'Working out the points…'}</p>;
  const p = data.points;
  const winnerId = data.contest.winnerMemberId;
  return (
    <div style={{ marginTop: 10 }}>
      {msg && <div className="note">{msg}</div>}
      <p className="qs" style={{ margin: '0 0 6px' }}>
        Live {p.live} · replay {p.replay} · like or comment on a contest post {p.engage} · picture entry {p.picture} a week.
        {!data.contest.eventWords && ' Live and replay points need the event name words (Edit).'}
      </p>
      {data.board.length === 0 ? <p className="qs">No points yet.</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead><tr style={{ textAlign: 'left' }}>
            <th style={{ padding: '4px 6px' }}>Member</th><th>Live</th><th>Replay</th><th>Hive</th><th>Pictures</th><th>Points</th><th />
          </tr></thead>
          <tbody>
            {data.board.map((b, i) => (
              <tr key={b.memberId} style={{ borderTop: '1px solid #eee5da', background: b.memberId === winnerId ? '#f6efe6' : 'transparent' }}>
                <td style={{ padding: '6px', opacity: b.eligible ? 1 : 0.55 }}>{b.eligible ? `${i + 1}. ` : ''}<b>{b.name}</b>{!b.eligible && <span className="qs"> · not eligible</span>}{b.memberId === winnerId && <b style={{ color: '#C4714A' }}> · 🏆 Winner</b>}</td>
                <td>{b.counts.live}</td><td>{b.counts.replay}</td><td>{b.counts.engage}</td><td>{b.counts.picture}</td>
                <td><b>{b.total}</b></td>
                <td style={{ textAlign: 'right' }}>
                  {b.memberId === winnerId
                    ? <button type="button" className="btn s" onClick={() => winner(null)}>Undo winner</button>
                    : b.eligible && <button type="button" className="btn s" onClick={() => winner(b.memberId)}>Winner</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="q" style={{ marginTop: 14, fontSize: 15 }}>My Hive posts that earn points</p>
      <p className="qs" style={{ marginTop: 0 }}>The contest post always counts. Add each week's homework post: open it, tap Copy link, and paste the link here.</p>
      {data.posts.map(pp => (
        <div key={pp.id} className="row" style={{ justifyContent: 'space-between', gap: 8, borderTop: '1px solid #eee5da', padding: '6px 0' }}>
          <span className="qs" style={{ margin: 0 }}>{pp.isContestPost ? '🏆 ' : ''}{pp.label}</span>
          {!pp.isContestPost && <button type="button" className="btn s" onClick={() => removePost(pp.id)}>Remove</button>}
        </div>
      ))}
      <div className="row" style={{ gap: 6, marginTop: 6 }}>
        <input value={link} onChange={e => setLink(e.target.value)} placeholder="Paste a My Hive post link" style={{ ...box, marginTop: 0, flex: 1 }} />
        <button type="button" className="btn s" disabled={!link.trim()} onClick={addPost}>Add post</button>
      </div>
    </div>
  );
}

export default function ContestsPage() {
  const [contests, setContests] = useState(null);
  const [magnets, setMagnets] = useState([]);
  const [people, setPeople] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | contest
  const [openId, setOpenId] = useState(null);
  const [pointsId, setPointsId] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => {
    api.get('/community/contests/manage').then(r => setContests(r.data.contests)).catch(() => setMsg('Could not load contests.'));
  }, []);
  useEffect(() => {
    load();
    api.get('/community/contests/manage/people').then(r => setPeople(r.data.people || [])).catch(() => {});
    api.get('/admin/sales-marketing/initiatives?kind=lead_magnet').then(r => setMagnets(r.data.initiatives || r.data || [])).catch(() => {});
  }, [load]);

  const toHive = async (c) => {
    setMsg(null);
    try {
      const r = await api.post(`/community/contests/manage/${c.id}/hive-post`);
      setMsg(r.data.updated ? 'The Hive post is up to date.' : 'Posted to My Hive.');
      load();
    } catch (e) { setMsg(e.response?.data?.error || 'Could not post to My Hive.'); }
  };
  const archive = async (c) => {
    if (!window.confirm(`Archive "${c.name}"? It comes off this page and the dashboard. Entries are kept.`)) return;
    await api.post(`/community/contests/manage/${c.id}/archive`); load();
  };

  return (
    <section>
      <h2 className="ptitle">Contests</h2>
      <p className="pq">
        Members-only contests live in My Hive: post the details there, and while a contest runs it shows as a 4th bubble on every member's dashboard.
        Members tick "This is my contest entry" when they post or comment. When a contest ends, a To-Do asks you to choose the winner.
      </p>
      {msg && <div className="note">{msg}</div>}
      {!editing && <button type="button" className="btn p s" onClick={() => setEditing({})}>+ New contest</button>}
      {editing && (
        <ContestForm
          start={editing}
          magnets={Array.isArray(magnets) ? magnets : []}
          people={people}
          onSaved={() => { setEditing(null); load(); }}
          onCancel={() => setEditing(null)}
        />
      )}
      {!contests ? <p className="pq">Loading…</p> : contests.length === 0 ? <p className="pq" style={{ marginTop: 12 }}>No contests yet.</p> : contests.map(c => (
        <div key={c.id} className="card" style={{ marginTop: 12 }}>
          <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
            {c.imageUrl && <img src={c.imageUrl} alt={c.imageAlt || ''} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 10, flexShrink: 0 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="q" style={{ margin: 0 }}>{c.name}</p>
              <p className="qs" style={{ margin: '2px 0' }}>
                {STATUS[c.status] || c.status} · {c.audience === 'members' ? 'Members only' : 'Lead magnet'} · {fmt(c.startsOn)} to {fmt(c.endsOn)} · {c.entryCount || 0} entr{c.entryCount === 1 ? 'y' : 'ies'}
                {c.winnerMethod === 'points' ? ' · most points wins' : ''}{c.winnerOn ? ` · winner chosen ${fmt(c.winnerOn)}` : ''}{(c.winnerEntryId || c.winnerMemberId) ? ' · 🏆 winner picked' : ''}
                {c.audience === 'members' && !c.hivePostId ? ' · not shown to members until it\'s posted to My Hive' : ''}
                {!c.rules ? ' · rules still needed' : ''}
              </p>
              {c.message && <p className="qs" style={{ margin: 0 }}>{c.message}</p>}
            </div>
          </div>
          <div className="row" style={{ gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            <button type="button" className="btn s" onClick={() => setEditing(c)}>Edit</button>
            {c.audience === 'members' && (
              <button type="button" className="btn s" onClick={() => toHive(c)}>{c.hivePostId ? 'Update the Hive post' : 'Post to My Hive'}</button>
            )}
            {c.hivePostId && <Link className="btn s" to={`/dashboard/discussion/${c.hivePostId}`}>Open the Hive post</Link>}
            {c.hivePostId && <CopyLinkButton postId={c.hivePostId} className="btn s" />}
            {c.winnerMethod === 'points' && (
              <button type="button" className="btn s" onClick={() => setPointsId(pointsId === c.id ? null : c.id)}>{pointsId === c.id ? 'Hide points' : 'Points'}</button>
            )}
            <button type="button" className="btn s" onClick={() => setOpenId(openId === c.id ? null : c.id)}>{openId === c.id ? 'Hide entries' : `Entries (${c.entryCount || 0})`}</button>
            <button type="button" className="btn s" onClick={() => archive(c)}>Archive</button>
          </div>
          {pointsId === c.id && <Points contest={c} onChanged={load} />}
          {openId === c.id && <Entries contest={c} onChanged={load} />}
        </div>
      ))}
    </section>
  );
}
